import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  CircularProgress,
  Alert,
  IconButton,
  Chip,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Pagination,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  Card,
  CardContent,
  Grid,
  Menu,
  ButtonGroup,
  Checkbox,
  ListItemText,
  OutlinedInput,
  SelectChangeEvent,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  CloudUpload as UploadIcon,
  Description as DescriptionIcon,
  Visibility as VisibilityIcon,
  ArrowDropDown as ArrowDropDownIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { paperlessApi } from '../services/api';
import type {
  PaperlessDocument,
  PaperlessDocumentType,
  PaperlessTag,
} from '../types/api';

const PaperlessBrowser: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialSearch = searchParams.get('search') || '';

  const [documents, setDocuments] = useState<PaperlessDocument[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [searchQuery, setSearchQuery] = useState(initialSearch);
  const [searchInput, setSearchInput] = useState(initialSearch);
  const [documentTypes, setDocumentTypes] = useState<PaperlessDocumentType[]>([]);
  const [selectedDocumentType, setSelectedDocumentType] = useState<number | ''>('');
  const [tags, setTags] = useState<PaperlessTag[]>([]);
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<PaperlessDocument | null>(null);
  const [workspaceSlug, setWorkspaceSlug] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [detailDocument, setDetailDocument] = useState<PaperlessDocument | null>(null);
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState<{ [key: number]: HTMLElement | null }>({});

  // Preview Dialog state
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewDocument, setPreviewDocument] = useState<PaperlessDocument | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewOriginal, setPreviewOriginal] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);

  useEffect(() => {
    loadDocumentTypes();
    loadTags();
  }, []);

  useEffect(() => {
    loadDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, searchQuery, selectedDocumentType, selectedTagIds]);

  const loadDocumentTypes = async () => {
    try {
      const response = await paperlessApi.getDocumentTypes();
      setDocumentTypes(response.data);
    } catch (err: any) {
      console.error('Fehler beim Laden der Dokumenttypen:', err);
    }
  };

  const loadTags = async () => {
    try {
      const response = await paperlessApi.getTags();
      setTags(response.data);
    } catch (err: any) {
      console.error('Fehler beim Laden der Tags:', err);
    }
  };

  const loadDocuments = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await paperlessApi.getDocuments({
        page,
        pageSize,
        query: searchQuery || undefined,
        documentTypeId: selectedDocumentType || undefined,
        tagIds: selectedTagIds.length > 0 ? selectedTagIds : undefined,
        ordering: '-created',
      });

      setDocuments(response.data.results);
      setTotalCount(response.data.count);
    } catch (err: any) {
      console.error('Fehler beim Laden der Dokumente:', err);
      if (err.response?.status === 400) {
        setError('Paperless-NGX ist nicht konfiguriert. Bitte konfigurieren Sie Ihre Paperless-Zugangsdaten in den Benutzereinstellungen.');
      } else {
        setError(err.response?.data?.error || 'Fehler beim Laden der Dokumente von Paperless-NGX');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setSearchQuery(searchInput);
    setPage(1);
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearchQuery('');
    setSelectedTagIds([]);
    setSelectedDocumentType('');
    setPage(1);
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const handleDownloadMenuOpen = (docId: number, event: React.MouseEvent<HTMLElement>) => {
    setDownloadMenuAnchor(prev => ({ ...prev, [docId]: event.currentTarget }));
  };

  const handleDownloadMenuClose = (docId: number) => {
    setDownloadMenuAnchor(prev => ({ ...prev, [docId]: null }));
  };

  const handleDownload = async (doc: PaperlessDocument, original: boolean = false) => {
    handleDownloadMenuClose(doc.id);

    try {
      const response = await paperlessApi.downloadDocument(doc.id, original);

      // Create blob from response
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);

      // Trigger download
      const link = document.createElement('a');
      link.href = url;
      const prefix = original ? 'original_' : '';
      link.download = doc.originalFileName || `${prefix}${doc.title}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Cleanup
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Fehler beim Herunterladen:', err);
      setError('Fehler beim Herunterladen des Dokuments');
    }
  };

  const handleOpenUploadDialog = (doc: PaperlessDocument) => {
    setSelectedDocument(doc);
    setWorkspaceSlug('');
    setUploadSuccess(null);
    setUploadDialogOpen(true);
  };

  const handleUploadToLLM = async () => {
    if (!selectedDocument || !workspaceSlug.trim()) {
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const response = await paperlessApi.uploadToAnythingLLM({
        paperlessDocumentId: selectedDocument.id,
        workspaceSlug: workspaceSlug.trim(),
      });

      if (response.data.success) {
        setUploadSuccess(response.data.message);
        setTimeout(() => {
          setUploadDialogOpen(false);
          setUploadSuccess(null);
        }, 2000);
      } else {
        setError(response.data.message);
      }
    } catch (err: any) {
      console.error('Fehler beim Upload:', err);
      setError(err.response?.data?.message || 'Fehler beim Upload zu AnythingLLM');
    } finally {
      setUploading(false);
    }
  };

  const handleViewDetails = async (doc: PaperlessDocument) => {
    try {
      const response = await paperlessApi.getDocument(doc.id);
      setDetailDocument(response.data);
      setDetailDialogOpen(true);
    } catch (err: any) {
      console.error('Fehler beim Laden der Dokumentdetails:', err);
      setError('Fehler beim Laden der Dokumentdetails');
    }
  };

  // Preview functions
  const handleOpenPreview = async (doc: PaperlessDocument, original: boolean = false) => {
    setPreviewDocument(doc);
    setPreviewOriginal(original);
    setPreviewDialogOpen(true);
    await loadPreview(doc.id, original);
  };

  const loadPreview = async (docId: number, original: boolean) => {
    setLoadingPreview(true);
    try {
      const response = await paperlessApi.previewDocument(docId, original);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);

      // Cleanup previous URL
      if (previewUrl) {
        window.URL.revokeObjectURL(previewUrl);
      }

      setPreviewUrl(url);
    } catch (err: any) {
      console.error('Fehler beim Laden der Vorschau:', err);
      setError('Fehler beim Laden der Dokumentvorschau');
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleClosePreview = () => {
    setPreviewDialogOpen(false);
    if (previewUrl) {
      window.URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    setPreviewDocument(null);
  };

  const handleTogglePreviewOriginal = async (original: boolean) => {
    if (previewDocument) {
      setPreviewOriginal(original);
      await loadPreview(previewDocument.id, original);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Paperless-NGX Dokumente
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={loadDocuments}
          disabled={loading}
        >
          Aktualisieren
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Dokumente durchsuchen..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchInput && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleClearSearch}>
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Dokumenttyp</InputLabel>
              <Select
                value={selectedDocumentType}
                onChange={(e) => {
                  setSelectedDocumentType(e.target.value as number | '');
                  setPage(1);
                }}
                label="Dokumenttyp"
              >
                <MenuItem value="">Alle Typen</MenuItem>
                {documentTypes.map((type) => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Tags</InputLabel>
              <Select
                multiple
                value={selectedTagIds}
                onChange={(e: SelectChangeEvent<number[]>) => {
                  setSelectedTagIds(e.target.value as number[]);
                  setPage(1);
                }}
                input={<OutlinedInput label="Tags" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(selected as number[]).map((tagId) => {
                      const tag = tags.find(t => t.id === tagId);
                      return tag ? (
                        <Chip key={tagId} label={tag.name} size="small" />
                      ) : null;
                    })}
                  </Box>
                )}
              >
                {tags.map((tag) => (
                  <MenuItem key={tag.id} value={tag.id}>
                    <Checkbox checked={selectedTagIds.includes(tag.id)} />
                    <ListItemText primary={tag.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleSearch}
              disabled={loading}
            >
              Suchen
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : documents.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            {searchQuery || selectedDocumentType || selectedTagIds.length > 0
              ? 'Keine Dokumente gefunden'
              : 'Keine Dokumente vorhanden'}
          </Typography>
        </Paper>
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Titel</TableCell>
                  <TableCell>Dokumenttyp</TableCell>
                  <TableCell>Tags</TableCell>
                  <TableCell>Erstellt</TableCell>
                  <TableCell align="right">Aktionen</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {documents.map((doc) => (
                  <TableRow key={doc.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <DescriptionIcon color="action" />
                        <Typography variant="body2">{doc.title}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {doc.documentTypeName ? (
                        <Chip
                          label={doc.documentTypeName}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {doc.tagNames.length > 0 ? (
                          doc.tagNames.slice(0, 3).map((tag, index) => (
                            <Chip key={index} label={tag} size="small" />
                          ))
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            -
                          </Typography>
                        )}
                        {doc.tagNames.length > 3 && (
                          <Chip
                            label={`+${doc.tagNames.length - 3}`}
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{formatDate(doc.created)}</TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <Tooltip title="Details anzeigen">
                          <IconButton
                            size="small"
                            onClick={() => handleViewDetails(doc)}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Dokument anzeigen">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleOpenPreview(doc, false)}
                          >
                            <OpenInNewIcon />
                          </IconButton>
                        </Tooltip>

                        {/* Download Button Group with Dropdown */}
                        <ButtonGroup size="small" variant="outlined">
                          <Tooltip title="Archiviert herunterladen">
                            <IconButton
                              size="small"
                              onClick={() => handleDownload(doc, false)}
                            >
                              <DownloadIcon />
                            </IconButton>
                          </Tooltip>
                          <IconButton
                            size="small"
                            onClick={(e) => handleDownloadMenuOpen(doc.id, e)}
                          >
                            <ArrowDropDownIcon />
                          </IconButton>
                        </ButtonGroup>
                        <Menu
                          anchorEl={downloadMenuAnchor[doc.id]}
                          open={Boolean(downloadMenuAnchor[doc.id])}
                          onClose={() => handleDownloadMenuClose(doc.id)}
                        >
                          <MenuItem onClick={() => handleDownload(doc, false)}>
                            Archiviert herunterladen
                          </MenuItem>
                          <MenuItem onClick={() => handleDownload(doc, true)}>
                            Original herunterladen
                          </MenuItem>
                        </Menu>

                        <Tooltip title="Zu AnythingLLM hochladen">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleOpenUploadDialog(doc)}
                          >
                            <UploadIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
              showFirstButton
              showLastButton
            />
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
            {totalCount} Dokumente gefunden (Seite {page} von {totalPages})
          </Typography>
        </>
      )}

      {/* Upload to AnythingLLM Dialog */}
      <Dialog
        open={uploadDialogOpen}
        onClose={() => !uploading && setUploadDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Zu AnythingLLM hochladen</DialogTitle>
        <DialogContent>
          {uploadSuccess ? (
            <Alert severity="success" sx={{ mt: 2 }}>
              {uploadSuccess}
            </Alert>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Dokument: <strong>{selectedDocument?.title}</strong>
              </Typography>
              <TextField
                autoFocus
                margin="dense"
                label="Workspace Slug"
                type="text"
                fullWidth
                value={workspaceSlug}
                onChange={(e) => setWorkspaceSlug(e.target.value)}
                placeholder="z.B. my-workspace"
                helperText="Geben Sie den Slug des AnythingLLM Workspace ein"
                disabled={uploading}
              />
              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)} disabled={uploading}>
            Abbrechen
          </Button>
          <Button
            onClick={handleUploadToLLM}
            variant="contained"
            disabled={!workspaceSlug.trim() || uploading || !!uploadSuccess}
            startIcon={uploading ? <CircularProgress size={20} /> : <UploadIcon />}
          >
            {uploading ? 'Lädt hoch...' : 'Hochladen'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Document Details Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DescriptionIcon />
            {detailDocument?.title}
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {detailDocument && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Informationen
                    </Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Dokumenttyp:
                        </Typography>
                        <Typography variant="body1">
                          {detailDocument.documentTypeName || '-'}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Korrespondent:
                        </Typography>
                        <Typography variant="body1">
                          {detailDocument.correspondentName || '-'}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Erstellt:
                        </Typography>
                        <Typography variant="body1">
                          {formatDate(detailDocument.created)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Geändert:
                        </Typography>
                        <Typography variant="body1">
                          {formatDate(detailDocument.modified)}
                        </Typography>
                      </Grid>
                      {detailDocument.archiveSerialNumber && (
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Archiv-Nummer:
                          </Typography>
                          <Typography variant="body1">
                            {detailDocument.archiveSerialNumber}
                          </Typography>
                        </Grid>
                      )}
                      {detailDocument.originalFileName && (
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Dateiname:
                          </Typography>
                          <Typography variant="body1">
                            {detailDocument.originalFileName}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
              {detailDocument.tagNames.length > 0 && (
                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Tags
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {detailDocument.tagNames.map((tag, index) => (
                          <Chip key={index} label={tag} />
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              )}
              {detailDocument.content && (
                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Inhalt (Vorschau)
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          maxHeight: 300,
                          overflow: 'auto',
                          whiteSpace: 'pre-wrap',
                          fontFamily: 'monospace',
                          fontSize: '0.85rem',
                        }}
                      >
                        {detailDocument.content.substring(0, 1000)}
                        {detailDocument.content.length > 1000 && '...'}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>Schließen</Button>
          {detailDocument && (
            <>
              <ButtonGroup>
                <Button
                  onClick={() => handleDownload(detailDocument, false)}
                  startIcon={<DownloadIcon />}
                >
                  Herunterladen
                </Button>
                <Button
                  size="small"
                  onClick={(e) => handleDownloadMenuOpen(detailDocument.id, e)}
                >
                  <ArrowDropDownIcon />
                </Button>
              </ButtonGroup>
              <Menu
                anchorEl={downloadMenuAnchor[detailDocument.id]}
                open={Boolean(downloadMenuAnchor[detailDocument.id])}
                onClose={() => handleDownloadMenuClose(detailDocument.id)}
              >
                <MenuItem onClick={() => handleDownload(detailDocument, false)}>
                  Archiviert herunterladen
                </MenuItem>
                <MenuItem onClick={() => handleDownload(detailDocument, true)}>
                  Original herunterladen
                </MenuItem>
              </Menu>
              <Button
                onClick={() => {
                  setDetailDialogOpen(false);
                  handleOpenUploadDialog(detailDocument);
                }}
                variant="contained"
                startIcon={<UploadIcon />}
              >
                Zu AnythingLLM
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Document Preview Dialog */}
      <Dialog
        open={previewDialogOpen}
        onClose={handleClosePreview}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: { height: '90vh' }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DescriptionIcon />
              <Typography variant="h6" component="span">
                {previewDocument?.title}
              </Typography>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={previewOriginal}
                  onChange={(e) => handleTogglePreviewOriginal(e.target.checked)}
                  disabled={loadingPreview}
                />
              }
              label={previewOriginal ? 'Original' : 'Archiviert'}
              labelPlacement="start"
            />
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
          {loadingPreview ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
              <CircularProgress />
            </Box>
          ) : previewUrl ? (
            <iframe
              src={previewUrl}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                flex: 1,
              }}
              title={previewDocument?.title || 'Dokumentvorschau'}
            />
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
              <Typography color="text.secondary">
                Keine Vorschau verfügbar
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePreview}>Schließen</Button>
          {previewDocument && (
            <>
              <Button
                onClick={() => handleDownload(previewDocument, previewOriginal)}
                startIcon={<DownloadIcon />}
              >
                {previewOriginal ? 'Original' : 'Archiviert'} herunterladen
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PaperlessBrowser;
